import { useEffect, useCallback } from 'react';
import { AnyAction } from 'redux';
import { useDispatch } from 'react-redux';
import { option, array } from 'fp-ts';
import { pipe, flow } from 'fp-ts/function';
import { isDefined } from 'utils/tsHelpers';
import { isEmpty } from '@iterable/utils';
import { Catalog, CatalogFieldDetails, CatalogQuery, FieldType } from 'modules/domains';
import { useFetchCatalogFields } from 'modules/catalogFieldTypes/hooks';
import { FieldOption } from 'modules/queryBuilder/legacyQueryBuilder/types';
import {
  initializeQueryResponse,
  toggleViewMode,
} from 'modules/queryBuilder/legacyQueryBuilder/actions';
import { useCatalogs } from 'modules/catalog/hooks';
import { useCatalogContext } from 'modules/catalog/catalogSelector';
import { useAngularCatalogQueryContext } from '../../contexts/AngularCatalogQueryContext';
import { useCatalogSelectionContext } from '../contexts/CatalogSelectionContext';

const getCatalogFieldType =
  (catalogFieldTypes: FieldOption[]) =>
  (catalogField?: string): FieldOption | undefined =>
    pipe(
      catalogFieldTypes,
      array.findFirst(({ name }) => name === catalogField),
      option.toUndefined,
    );

const getCatalog = (catalogName?: string): ((catalogs: Catalog[]) => option.Option<Catalog>) =>
  flow(array.findFirst(({ name }) => name === catalogName));

const isDefinedCatalogQuery = (catalogQuery?: CatalogQuery) =>
  isDefined(catalogQuery) && !isEmpty(catalogQuery);

const getCatalogFieldDetails: (
  catalogFieldType?: FieldOption,
) => option.Option<CatalogFieldDetails> = flow(
  option.fromNullable,
  option.map(({ name, type }) => ({ field: name, fieldType: type })),
  option.alt(() => option.some({ fieldType: FieldType.STRING as FieldType })),
);

export const useUpdateCatalogDrawer = () => {
  const dispatch = useDispatch();
  const { catalogQueryDetails } = useAngularCatalogQueryContext();
  const { update } = useCatalogSelectionContext();
  const { change, changeFieldDetails } = useCatalogContext();
  const { data: catalogs = [], isLoading } = useCatalogs();
  const { data: catalogFieldTypes = [], isLoading: catalogFieldLoading } = useFetchCatalogFields(
    catalogQueryDetails?.catalogQuery?.catalogName,
  );

  const toggleBuilderView = useCallback(() => dispatch(toggleViewMode() as AnyAction), []);
  const initializeCatalogQuery = useCallback(
    (searchQueries: any, combinator: any) =>
      dispatch(
        initializeQueryResponse({
          searchQueries,
          combinator,
          hasDynamicFields: false,
        }) as AnyAction,
      ),
    [],
  );

  useEffect(() => {
    const catalogQuery = catalogQueryDetails?.catalogQuery;
    const catalog = getCatalog(catalogQuery?.catalogName)(catalogs);
    const catalogFieldType = getCatalogFieldType(catalogFieldTypes)(
      catalogQueryDetails?.catalogField,
    );
    const catalogFieldDetails = getCatalogFieldDetails(catalogFieldType);

    const hasCatalogFieldType = !isDefined(catalogFieldType);
    const hasCatalogQuery = isDefinedCatalogQuery(catalogQuery);
    const hasCatalogState = option.isSome(catalog) && option.isSome(catalogFieldDetails);

    const canUpdateCatalogDrawer = hasCatalogState && hasCatalogQuery;

    if (canUpdateCatalogDrawer && catalogQuery) {
      const {
        searchQuery: { combinator, searchQueries } = {},
      } = catalogQuery;
      change(catalog); // update catalog
      changeFieldDetails(catalogFieldDetails); // update catalog field details
      initializeCatalogQuery(searchQueries, combinator); // update builder
      toggleBuilderView(); // update builder view
      update(hasCatalogFieldType); // update catalog match (key or property)
    }
  }, [catalogQueryDetails, isLoading, catalogFieldLoading]);
};
